<template>
    <div :class="['editor', { 'disabled' : !editable}]" :editable="editable" :autoFocus="autoFocus">
        <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }" v-if="editable">
            <div class="menubar">
                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.bold() }"
                    @click="commands.bold"
                >
                    <span class="icon-bold" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.italic() }"
                    @click="commands.italic"
                >
                    <span class="icon-italic" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.underline() }"
                    @click="commands.underline"
                >
                    <span class="icon-underline" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.code() }"
                    @click="commands.code"
                >
                    <span class="icon-code" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.paragraph() }"
                    @click="commands.paragraph"
                >
                    <span class="icon-paragraph" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 1 }) }"
                    @click="commands.heading({ level: 1 })"
                >
                    H1
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 2 }) }"
                    @click="commands.heading({ level: 2 })"
                >
                    H2
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 3 }) }"
                    @click="commands.heading({ level: 3 })"
                >
                    H3
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.bullet_list() }"
                    @click="commands.bullet_list"
                >
                    <span class="icon-ul" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.ordered_list() }"
                    @click="commands.ordered_list"
                >
                    <span class="icon-ol" />
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.blockquote() }"
                    @click="commands.blockquote"
                >
                    <span class="icon-quote" />
                </button>

                <button
                    class="menubar__button"
                    @click="commands.horizontal_rule"
                >
                    <span class="icon-hr" />
                </button>

                <button
                    class="menubar__button"
                    @click="commands.undo"
                >
                    <span class="icon-undo" />
                </button>

                <button
                    class="menubar__button"
                    @click="commands.redo"
                >
                    <span class="icon-redo" />
                </button>

            </div>
        </editor-menu-bar>
        <editor-content class="editor__content" id="editor-content" :editor="editor" :content="content" :style="cssVars" :height="height" />
    </div>
</template>

<script>
    import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
    import {
        Blockquote,
        HardBreak,
        Heading,
        HorizontalRule,
        OrderedList,
        BulletList,
        ListItem,
        TodoItem,
        TodoList,
        Bold,
        Code,
        Italic,
        Link,
        Underline,
        History
    } from 'tiptap-extensions'

    export default {
        components: {
            EditorContent,
            EditorMenuBar
        },
        props: {
            height: {
                type: Number,
                default: 116
            },
            editable: {
                type: Boolean,
                default: true
            },
            autoFocus: {
                type: Boolean,
                default: false
            },
            content: {
                type: String,
                default: '<p></p>'
            }
        },
        data () {
            return {
                editor: new Editor({
                    editable: this.editable,
                    autoFocus: this.autoFocus,
                    extensions: [
                        new Blockquote(),
                        new BulletList(),
                        new HardBreak(),
                        new Heading({ levels: [ 1, 2, 3 ] }),
                        new HorizontalRule(),
                        new ListItem(),
                        new OrderedList(),
                        new TodoItem(),
                        new TodoList(),
                        new Link(),
                        new Bold(),
                        new Code(),
                        new Italic(),
                        new Underline(),
                        new History()
                    ],
                    content: this.content,
                    parseOptions: {
                        preserveWhitespace: 'full'
                    },
                    onUpdate: ({ getHTML }) => {
                        this.$emit('input', getHTML())
                    }
                })
            }
        },
        watch: {
            editable () {
                this.editor.setOptions({
                    editable: this.editable
                })
                this.editor.focus()
            },
            content: {
                handler: 'setContent',
                immediate: true
            }
        },
        methods: {
            setContent (newContent) {
                if (newContent === this.editor.getHTML()) {
                    return
                }
                this.editor.setContent(newContent)
                this.$emit('input', newContent)
            }
        },
        computed: {
            cssVars () {
                return {
                    '--height': this.height + 'px'
                }
            }
        },
        beforeDestroy () {
            this.editor.destroy()
        }
    }
</script>

<style lang="scss">
    .ProseMirror {
        min-height: inherit;

        ul {
            list-style: disc;
        }
    }

    .editor {
        border: 1px solid $emperor;
        background: $white;
        position: relative;
        padding: 10px 0 0 20px;
        border-radius: 5px;
        flex: 1;
        
        &__content {
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;

            min-height: var(--height);
            max-height: var(--height);
            overflow-y: auto;

            * {
                caret-color: currentColor;
            }

            p code {
                display: inline-block;
                padding: 0.5rem 1rem;
                border-radius: 5px;
                font-size: 1.4rem;
                font-weight: bold;
                @include black(background, 0.1);
                @include black(color, 0.8);

            }

            ul,
            ol {
                padding: 0 0 0 2rem;
            }

            li > p,
            li > ol,
            li > ul {
                margin: 0;
            }

            a {
                color: inherit;
            }

            blockquote {
                border-left: 3px solid rgba(0, 0, 0, 0.1);
                @include black(color, 0.8);
                padding-left: 0.8rem;
                font-style: italic;

                p {
                    margin: 0;
                }
            }
        }

        div:focus {
            outline: none;
        }
    }

    .menubar {
        margin-bottom: 10px;
        transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;

        &.is-hidden {
            visibility: hidden;
            opacity: 0;
        }

        &.is-focused {
            visibility: visible;
            opacity: 1;
            transition: visibility 0.2s, opacity 0.2s;
        }

        &__button {
            font-weight: bold;
            display: inline-flex;
            background: transparent;
            border: 0;
            color: $black;
            padding: 0.5rem 1rem;
            margin-right: 0.5rem;
            border-radius: 3px;
            cursor: pointer;

            &:first-of-type {
              padding-left: 0;
            }

            &:hover {
                @include black(background, 0.05);
            }

            &.is-active {
                @include black(background, 0.1);
            }
        }
    }

</style>
